import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import type { QueryParamType } from '@app/src/hooks/queryState'
import useActiveRiskIndexByType from '@app/src/hooks/useActiveRiskIndexByType'
import { useAssessmentTemplatesWithSections } from '@app/src/hooks/useAssessmentTemplatesWithSections'
import { useRiskStatus } from '@app/src/hooks/useRiskStatus'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'
import { AUTOMATION_QUESTION_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import { mapToQueryParamType, normalizeFilterGroups } from '@app/src/pages/QuestionEditor/AnswerClassification/utils'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSelect from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { getFilterOptionsForLevels, LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import { Grid, Typography } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import useDeepCompareEffect from 'use-deep-compare-effect'

const AutomationAssessments: React.FC = () => {
  const { setValue, watch, getValues } = useFormContext()
  const { formatMessage } = useIntl()
  const { templateOptions, assessmentTemplates } = useAssessmentTemplatesWithSections()
  const filters = useGetApiQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)
  const hasInitializedRef = React.useRef(false)
  const { riskStatusOptions } = useRiskStatus()
  const { countryOptions, industryOptions } = useActiveRiskIndexByType()
  const isAiScreeningEnabled = useAiScreeningEnabled()

  const initialFilterValues: FilterGroup[] | undefined = useMemo(
    () => getValues(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS),
    [],
  )

  const { setQueryFilters } = useQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)

  useDeepCompareEffect(() => {
    if (hasInitializedRef.current) return

    const source = initialFilterValues?.length ? initialFilterValues : filters
    const normalized = normalizeFilterGroups(source)

    const mappedFilters = normalized.map(mapToQueryParamType).filter((f): f is QueryParamType => f !== undefined)

    setQueryFilters(mappedFilters)
    setValue(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS, normalized)

    hasInitializedRef.current = true
  }, [initialFilterValues, filters])

  useDeepCompareEffect(() => {
    const normalized = normalizeFilterGroups(filters)
    setValue(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS, normalized)
  }, [filters])

  const categoryFilters = watch(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS)

  const selectedTemplateFilter = categoryFilters?.find(
    (filter: FilterGroup) => filter.name === 'assessments.assessmentTemplate.id',
  )
  const selectedTemplateId = selectedTemplateFilter?.filters?.[0]?.value
  const selectedTemplate = assessmentTemplates?.find(t => t.id === Number(selectedTemplateId))

  const renderFilter = useCallback(
    (name: string, label: string, options: { label: string; value: string | number }[], shouldFormatLabel = true) => (
      <Grid item xs={6} key={name}>
        <Filter operator={Operators.In} name={name}>
          {({ value, onChange }) => (
            <FilterSelect
              size="small"
              multiple
              options={options}
              onChange={val => onChange(val as string[])}
              fieldLabel={shouldFormatLabel ? formatMessage({ id: label }) : label}
              value={[value].flat()}
            />
          )}
        </Filter>
      </Grid>
    ),
    [formatMessage],
  )

  const renderSectionFilters = useCallback(() => {
    if (!selectedTemplate?.sections) return null

    const sectionLevelOptions = getFilterOptionsForLevels(
      selectedTemplate.assessmentType,
      selectedTemplate.levels ?? 5,
      formatMessage,
      selectedTemplate.levelNamesType,
      LabelFormat.Basic,
    )

    return selectedTemplate.sections.map(section => {
      const prop = `assessments.section${section.assessmentSectionNumber}Level`
      const label = section.name
      return renderFilter(prop, label, sectionLevelOptions, false)
    })
  }, [selectedTemplate, formatMessage, renderFilter])

  const renderTotalLevelFilter = useCallback(() => {
    if (!selectedTemplate) return null

    const totalLevelOptions = getFilterOptionsForLevels(
      selectedTemplate.assessmentType,
      selectedTemplate.levels ?? 5,
      formatMessage,
      selectedTemplate.levelNamesType,
      LabelFormat.Basic,
    )

    return renderFilter('assessments.totalLevel', 'assessment.filter.result', totalLevelOptions)
  }, [selectedTemplate, formatMessage, renderFilter])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle1">
          {formatMessage({ id: 'form.createAutomation.automationType.providersWithNewAssessmentLevel' })}
        </Typography>
      </Grid>

      <Grid item xs={6} key="assessments.assessmentTemplate.id">
        <Filter operator={Operators.EqualTo} name="assessments.assessmentTemplate.id">
          {({ value, onChange }) => (
            <FilterSelect
              size="small"
              multiple={false}
              options={templateOptions}
              onChange={value => onChange(value)}
              fieldLabel={formatMessage({ id: 'assessment.filter.name' })}
              value={value?.toString()}
            />
          )}
        </Filter>
      </Grid>

      {renderTotalLevelFilter()}

      {selectedTemplate?.assessmentType === AssessmentType.Flexible ||
      selectedTemplate?.assessmentType === AssessmentType.BaselineAssessment
        ? renderSectionFilters()
        : null}

      {isAiScreeningEnabled && selectedTemplate?.assessmentType === AssessmentType.BaselineAssessment && (
        <Grid item xs={12} key="schemas.request.source" sx={{ marginBottom: 4 }}>
          <Filter operator={Operators.EqualTo} name="assessments.isScreened">
            {({ value, onChange }) => (
              <FilterSelect
                size="small"
                multiple={false}
                options={[
                  { value: true, label: formatMessage({ id: 'general.aiScreening' }) },
                  { value: false, label: formatMessage({ id: 'general.selfReported' }) },
                ]}
                onChange={value => onChange(value)}
                fieldLabel={formatMessage({ id: 'schemas.request.source' })}
                value={Array.isArray(value) ? value?.[0] : value}
                helperText={formatMessage({ id: 'form.createAutomation.automationType.sourceHelperText' })}
              />
            )}
          </Filter>
        </Grid>
      )}
      {(countryOptions.length > 0 || industryOptions.length > 0) && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1">{formatMessage({ id: 'schemas.risk.riskStatusFilter' })}</Typography>
          </Grid>
          {countryOptions.length > 0 && (
            <>
              <Grid item xs={6} key="countryIndex">
                <Filter operator={Operators.EqualTo} name="countryIndex">
                  {({ value, onChange }) => (
                    <FilterSelect
                      size="small"
                      multiple={false}
                      options={countryOptions}
                      onChange={value => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.risk.countryRiskIndex' })}
                      value={value?.toString()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item xs={6} key="country">
                <Filter operator={Operators.In} name="country">
                  {({ value, onChange }) => (
                    <FilterSelect
                      size="small"
                      multiple
                      options={riskStatusOptions}
                      onChange={val => onChange(val as string[])}
                      fieldLabel={formatMessage({ id: 'schemas.risk.countryRiskStatusFilter' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
            </>
          )}
          {industryOptions.length > 0 && (
            <>
              <Grid item xs={6} key="industryIndex">
                <Filter operator={Operators.EqualTo} name="industryIndex">
                  {({ value, onChange }) => (
                    <FilterSelect
                      size="small"
                      multiple={false}
                      options={industryOptions}
                      onChange={value => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.risk.industryRiskIndex' })}
                      value={value?.toString()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item xs={6} key="industry">
                <Filter operator={Operators.In} name="industry">
                  {({ value, onChange }) => (
                    <FilterSelect
                      size="small"
                      multiple
                      options={riskStatusOptions}
                      onChange={val => onChange(val as string[])}
                      fieldLabel={formatMessage({ id: 'schemas.risk.industryRiskStatusFilter' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  )
}

export default AutomationAssessments
